import React, { useEffect, useState, useRef } from "react";
import {
  Plus,
  Trash2,
  ArrowUp,
  ArrowDown,
  Upload,
  Loader2,
  Save,
  ImageIcon,
  CheckCircle2,
  AlertCircle,
  LayoutGrid,
} from "lucide-react";
import { API, CATEGORY_SLUGS, categoryLabel, resolveImage } from "../products";

const TOKEN_KEY = "tcs_admin_token";
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` });

function Toast({ toast, onDone }) {
  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(onDone, 2600);
    return () => clearTimeout(id);
  }, [toast, onDone]);
  if (!toast) return null;
  const ok = toast.type !== "error";
  return (
    <div
      data-testid="site-cat-toast"
      className={`fixed bottom-6 right-6 z-[200] flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-white font-semibold ${ok ? "bg-green-600" : "bg-red-600"}`}
    >
      {ok ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
      {toast.msg}
    </div>
  );
}

const CategoryRow = ({ cat, index, total, onChange, onSave, onDelete, onMove, notify }) => {
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await fetch(`${API}/admin/upload-image`, { method: "POST", headers: authHeaders(), body: fd });
      if (!res.ok) throw new Error("upload");
      const data = await res.json();
      onChange({ ...cat, image: data.image_url });
      notify("ok", "Image uploaded — click Save to publish");
    } catch (e) {
      notify("error", "Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!cat.name_en.trim()) { notify("error", "English name is required"); return; }
    setSaving(true);
    await onSave(cat);
    setSaving(false);
  };

  return (
    <div
      data-testid={`site-cat-row-${cat.id}`}
      className="flex flex-col sm:flex-row gap-4 bg-white rounded-2xl border border-slate-200 p-4"
    >
      {/* Image */}
      <div className="shrink-0">
        <div className="relative w-full sm:w-40 aspect-[3/4] rounded-xl overflow-hidden bg-slate-100 border border-slate-200">
          {cat.image ? (
            <img src={resolveImage(cat.image)} alt={cat.name_en} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-slate-300">
              <ImageIcon className="w-8 h-8" />
            </div>
          )}
          {uploading && (
            <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-green-600" />
            </div>
          )}
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
          data-testid={`site-cat-file-${cat.id}`}
        />
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid={`site-cat-upload-${cat.id}`}
          className="mt-2 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 text-slate-700 font-bold text-sm hover:border-green-400 disabled:opacity-60"
        >
          <Upload className="w-4 h-4" /> Change photo
        </button>
      </div>

      {/* Fields */}
      <div className="flex-1 grid sm:grid-cols-2 gap-3 min-w-0">
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Name (English)</label>
          <input
            value={cat.name_en}
            onChange={(e) => onChange({ ...cat, name_en: e.target.value })}
            data-testid={`site-cat-name-en-${cat.id}`}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 outline-none focus:ring-2 focus:ring-green-500/40 focus:border-green-500"
          />
        </div>
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Name (Spanish)</label>
          <input
            value={cat.name_es}
            onChange={(e) => onChange({ ...cat, name_es: e.target.value })}
            data-testid={`site-cat-name-es-${cat.id}`}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 outline-none focus:ring-2 focus:ring-green-500/40 focus:border-green-500"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Links to catalog category</label>
          <select
            value={cat.link_category}
            onChange={(e) => onChange({ ...cat, link_category: e.target.value })}
            data-testid={`site-cat-link-${cat.id}`}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 bg-white outline-none focus:ring-2 focus:ring-green-500/40 focus:border-green-500"
          >
            {CATEGORY_SLUGS.map((s) => (
              <option key={s} value={s}>{categoryLabel(s, "en")}</option>
            ))}
          </select>
          <p className="mt-1 text-xs text-slate-400">Clicking this tile opens the catalog filtered to this category.</p>
        </div>

        <div className="sm:col-span-2 flex items-center gap-2 flex-wrap">
          <button
            onClick={save}
            disabled={saving}
            data-testid={`site-cat-save-${cat.id}`}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white font-bold text-sm disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
          </button>
          <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
            <button
              onClick={() => onMove(index, -1)}
              disabled={index === 0}
              data-testid={`site-cat-up-${cat.id}`}
              title="Move up"
              className="px-2.5 py-2 text-slate-600 hover:bg-slate-100 disabled:opacity-40"
            >
              <ArrowUp className="w-4 h-4" />
            </button>
            <button
              onClick={() => onMove(index, 1)}
              disabled={index === total - 1}
              data-testid={`site-cat-down-${cat.id}`}
              title="Move down"
              className="px-2.5 py-2 text-slate-600 hover:bg-slate-100 border-l border-slate-200 disabled:opacity-40"
            >
              <ArrowDown className="w-4 h-4" />
            </button>
          </div>
          <button
            onClick={() => onDelete(cat)}
            data-testid={`site-cat-delete-${cat.id}`}
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-red-200 text-red-600 font-bold text-sm hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4" /> Delete
          </button>
        </div>
      </div>
    </div>
  );
};

export default function AdminSiteCategories() {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const notify = (type, msg) => setToast({ type, msg });

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/site/categories`);
      const data = await res.json();
      setCats(Array.isArray(data) ? data : []);
    } catch (e) {
      notify("error", "Failed to load categories");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onChange = (updated) => setCats((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));

  const onSave = async (cat) => {
    try {
      const res = await fetch(`${API}/admin/site/categories/${cat.id}`, {
        method: "PUT",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          name_en: cat.name_en,
          name_es: cat.name_es,
          image: cat.image,
          link_category: cat.link_category,
        }),
      });
      if (!res.ok) throw new Error("save");
      notify("ok", "Tile saved");
    } catch (e) {
      notify("error", "Save failed");
    }
  };

  const onDelete = async (cat) => {
    if (!window.confirm(`Delete "${cat.name_en}" tile?`)) return;
    try {
      const res = await fetch(`${API}/admin/site/categories/${cat.id}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error("delete");
      setCats((prev) => prev.filter((c) => c.id !== cat.id));
      notify("ok", "Tile deleted");
    } catch (e) {
      notify("error", "Delete failed");
    }
  };

  const onAdd = async () => {
    try {
      const res = await fetch(`${API}/admin/site/categories`, {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ name_en: "New Category", name_es: "Nueva Categoría", image: "", link_category: "other" }),
      });
      if (!res.ok) throw new Error("add");
      const created = await res.json();
      setCats((prev) => [...prev, created]);
      notify("ok", "Tile added — set a name and photo");
    } catch (e) {
      notify("error", "Could not add tile");
    }
  };

  const onMove = async (index, dir) => {
    const next = [...cats];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setCats(next);
    try {
      await fetch(`${API}/admin/site/categories/reorder`, {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ ids: next.map((c) => c.id) }),
      });
    } catch (e) {
      notify("error", "Reorder failed");
      load();
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-site-categories">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <LayoutGrid className="w-5 h-5 text-green-600" />
          <div>
            <h2 className="font-extrabold text-slate-800 text-lg">Homepage Categories</h2>
            <p className="text-sm text-slate-500">Manage the "What We Offer" tiles — names, photos, order &amp; links.</p>
          </div>
        </div>
        <button
          onClick={onAdd}
          data-testid="site-cat-add"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-green-600 hover:bg-green-700 text-white font-bold text-sm"
        >
          <Plus className="w-4 h-4" /> Add tile
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…
        </div>
      ) : cats.length === 0 ? (
        <div className="text-center py-20 text-slate-400" data-testid="site-cat-empty">
          No tiles yet. Click "Add tile" to create one.
        </div>
      ) : (
        <div className="space-y-4">
          {cats.map((cat, i) => (
            <CategoryRow
              key={cat.id}
              cat={cat}
              index={i}
              total={cats.length}
              onChange={onChange}
              onSave={onSave}
              onDelete={onDelete}
              onMove={onMove}
              notify={notify}
            />
          ))}
        </div>
      )}

      <Toast toast={toast} onDone={() => setToast(null)} />
    </div>
  );
}
